import { useCallback } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import classNames from 'classnames';

import type { Editor } from '@tiptap/react';
import { useEditorState } from '@tiptap/react';

import FormatQuoteIcon from '@/material-icons/400-24px/format_quote.svg?react';
import LinkIcon from '@/material-icons/400-24px/link.svg?react';
import { Icon } from 'mastodon/components/icon';

import { MAX_INDENT_LEVEL } from '../util/indent_extension';

const messages = defineMessages({
  bold: { id: 'rich_text_editor.bold', defaultMessage: 'Bold' },
  italic: { id: 'rich_text_editor.italic', defaultMessage: 'Italic' },
  underline: { id: 'rich_text_editor.underline', defaultMessage: 'Underline' },
  strike: { id: 'rich_text_editor.strike', defaultMessage: 'Strikethrough' },
  code: { id: 'rich_text_editor.code', defaultMessage: 'Code' },
  blockquote: { id: 'rich_text_editor.blockquote', defaultMessage: 'Quote' },
  bulletList: {
    id: 'rich_text_editor.bullet_list',
    defaultMessage: 'Bulleted list',
  },
  orderedList: {
    id: 'rich_text_editor.ordered_list',
    defaultMessage: 'Numbered list',
  },
  link: { id: 'rich_text_editor.link', defaultMessage: 'Link' },
  linkPrompt: {
    id: 'rich_text_editor.link_prompt',
    defaultMessage: 'Enter a URL, or leave blank to remove the link',
  },
  indent: { id: 'rich_text_editor.indent', defaultMessage: 'Indent' },
  outdent: { id: 'rich_text_editor.outdent', defaultMessage: 'Outdent' },
  clearFormatting: {
    id: 'rich_text_editor.clear_formatting',
    defaultMessage: 'Clear formatting',
  },
  paragraphStyle: {
    id: 'rich_text_editor.paragraph_style',
    defaultMessage: 'Paragraph style',
  },
  paragraph: {
    id: 'rich_text_editor.paragraph_style.paragraph',
    defaultMessage: 'Paragraph',
  },
  heading1: {
    id: 'rich_text_editor.paragraph_style.heading1',
    defaultMessage: 'Heading 1',
  },
  heading2: {
    id: 'rich_text_editor.paragraph_style.heading2',
    defaultMessage: 'Heading 2',
  },
  heading3: {
    id: 'rich_text_editor.paragraph_style.heading3',
    defaultMessage: 'Heading 3',
  },
});

type ParagraphStyle = 'paragraph' | 'heading1' | 'heading2' | 'heading3';

const HEADING_LEVEL_BY_STYLE = {
  heading1: 1,
  heading2: 2,
  heading3: 3,
} as const;

// Prevent a toolbar button's mousedown from stealing focus/collapsing the
// editor's selection before the click handler runs (standard rich-text
// toolbar pattern).
const preventMouseDownDefault = (e: React.MouseEvent) => {
  e.preventDefault();
};

export const ComposeToolbar: React.FC<{ editor: Editor }> = ({ editor }) => {
  const intl = useIntl();

  const state = useEditorState({
    editor,
    selector: ({ editor: currentEditor }) => ({
      isBold: currentEditor.isActive('bold'),
      isItalic: currentEditor.isActive('italic'),
      isUnderline: currentEditor.isActive('underline'),
      isStrike: currentEditor.isActive('strike'),
      isCode: currentEditor.isActive('code'),
      isBlockquote: currentEditor.isActive('blockquote'),
      isBulletList: currentEditor.isActive('bulletList'),
      isOrderedList: currentEditor.isActive('orderedList'),
      isLink: currentEditor.isActive('link'),
      indentLevel:
        (currentEditor.state.selection.$from.parent.attrs.indentLevel as
          | number
          | null) ?? 0,
      paragraphStyle: currentEditor.isActive('heading', { level: 1 })
        ? 'heading1'
        : currentEditor.isActive('heading', { level: 2 })
          ? 'heading2'
          : currentEditor.isActive('heading', { level: 3 })
            ? 'heading3'
            : 'paragraph',
    }),
  });

  const toggleBold = useCallback(() => {
    editor.chain().focus().toggleBold().run();
  }, [editor]);

  const toggleItalic = useCallback(() => {
    editor.chain().focus().toggleItalic().run();
  }, [editor]);

  const toggleUnderline = useCallback(() => {
    editor.chain().focus().toggleUnderline().run();
  }, [editor]);

  const toggleStrike = useCallback(() => {
    editor.chain().focus().toggleStrike().run();
  }, [editor]);

  const toggleCode = useCallback(() => {
    editor.chain().focus().toggleCode().run();
  }, [editor]);

  const toggleBlockquote = useCallback(() => {
    editor.chain().focus().toggleBlockquote().run();
  }, [editor]);

  const toggleBulletList = useCallback(() => {
    editor.chain().focus().toggleBulletList().run();
  }, [editor]);

  const toggleOrderedList = useCallback(() => {
    editor.chain().focus().toggleOrderedList().run();
  }, [editor]);

  const setLink = useCallback(() => {
    const previousUrl = editor.getAttributes('link').href as string | undefined;
    // A native prompt is the simplest correct way to collect a URL here;
    // there's no existing modal/popover component in this codebase to reuse
    // for a one-field input, and the server-side sanitizer is the actual
    // security boundary regardless of what's typed here.
    const url = window.prompt(
      intl.formatMessage(messages.linkPrompt),
      previousUrl ?? '',
    );

    if (url === null) return;

    const chain = editor.chain().focus().extendMarkRange('link');

    if (url.trim() === '') {
      chain.unsetLink().run();
    } else {
      chain.setLink({ href: url.trim() }).run();
    }
  }, [editor, intl]);

  const handleParagraphStyleChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const value = e.target.value as ParagraphStyle;

      if (value === 'paragraph') {
        editor.chain().focus().setParagraph().run();
      } else {
        editor
          .chain()
          .focus()
          .setHeading({ level: HEADING_LEVEL_BY_STYLE[value] })
          .run();
      }
    },
    [editor],
  );

  const increaseIndent = useCallback(() => {
    editor.chain().focus().increaseIndent().run();
  }, [editor]);

  const decreaseIndent = useCallback(() => {
    editor.chain().focus().decreaseIndent().run();
  }, [editor]);

  const clearFormatting = useCallback(() => {
    editor.chain().focus().clearNodes().unsetAllMarks().run();
  }, [editor]);

  return (
    <div className='rich-text-toolbar'>
      <select
        className='rich-text-toolbar__select'
        aria-label={intl.formatMessage(messages.paragraphStyle)}
        value={state.paragraphStyle}
        onChange={handleParagraphStyleChange}
      >
        <option value='paragraph'>
          {intl.formatMessage(messages.paragraph)}
        </option>
        <option value='heading1'>
          {intl.formatMessage(messages.heading1)}
        </option>
        <option value='heading2'>
          {intl.formatMessage(messages.heading2)}
        </option>
        <option value='heading3'>
          {intl.formatMessage(messages.heading3)}
        </option>
      </select>

      <button
        type='button'
        className={classNames('rich-text-toolbar__button', {
          active: state.isBold,
        })}
        title={intl.formatMessage(messages.bold)}
        aria-label={intl.formatMessage(messages.bold)}
        aria-pressed={state.isBold}
        onMouseDown={preventMouseDownDefault}
        onClick={toggleBold}
      >
        <span className='rich-text-toolbar__glyph rich-text-toolbar__glyph--bold'>
          B
        </span>
      </button>

      <button
        type='button'
        className={classNames('rich-text-toolbar__button', {
          active: state.isItalic,
        })}
        title={intl.formatMessage(messages.italic)}
        aria-label={intl.formatMessage(messages.italic)}
        aria-pressed={state.isItalic}
        onMouseDown={preventMouseDownDefault}
        onClick={toggleItalic}
      >
        <span className='rich-text-toolbar__glyph rich-text-toolbar__glyph--italic'>
          I
        </span>
      </button>

      <button
        type='button'
        className={classNames('rich-text-toolbar__button', {
          active: state.isUnderline,
        })}
        title={intl.formatMessage(messages.underline)}
        aria-label={intl.formatMessage(messages.underline)}
        aria-pressed={state.isUnderline}
        onMouseDown={preventMouseDownDefault}
        onClick={toggleUnderline}
      >
        <span className='rich-text-toolbar__glyph rich-text-toolbar__glyph--underline'>
          U
        </span>
      </button>

      <button
        type='button'
        className={classNames('rich-text-toolbar__button', {
          active: state.isStrike,
        })}
        title={intl.formatMessage(messages.strike)}
        aria-label={intl.formatMessage(messages.strike)}
        aria-pressed={state.isStrike}
        onMouseDown={preventMouseDownDefault}
        onClick={toggleStrike}
      >
        <span className='rich-text-toolbar__glyph rich-text-toolbar__glyph--strike'>
          S
        </span>
      </button>

      <button
        type='button'
        className={classNames('rich-text-toolbar__button', {
          active: state.isCode,
        })}
        title={intl.formatMessage(messages.code)}
        aria-label={intl.formatMessage(messages.code)}
        aria-pressed={state.isCode}
        onMouseDown={preventMouseDownDefault}
        onClick={toggleCode}
      >
        <span className='rich-text-toolbar__glyph rich-text-toolbar__glyph--code'>
          {'</>'}
        </span>
      </button>

      <button
        type='button'
        className={classNames('rich-text-toolbar__button', {
          active: state.isBlockquote,
        })}
        title={intl.formatMessage(messages.blockquote)}
        aria-label={intl.formatMessage(messages.blockquote)}
        aria-pressed={state.isBlockquote}
        onMouseDown={preventMouseDownDefault}
        onClick={toggleBlockquote}
      >
        <Icon id='quote' icon={FormatQuoteIcon} />
      </button>

      <button
        type='button'
        className={classNames('rich-text-toolbar__button', {
          active: state.isBulletList,
        })}
        title={intl.formatMessage(messages.bulletList)}
        aria-label={intl.formatMessage(messages.bulletList)}
        aria-pressed={state.isBulletList}
        onMouseDown={preventMouseDownDefault}
        onClick={toggleBulletList}
      >
        <span className='rich-text-toolbar__glyph rich-text-toolbar__glyph--bullet-list'>
          ·≡
        </span>
      </button>

      <button
        type='button'
        className={classNames('rich-text-toolbar__button', {
          active: state.isOrderedList,
        })}
        title={intl.formatMessage(messages.orderedList)}
        aria-label={intl.formatMessage(messages.orderedList)}
        aria-pressed={state.isOrderedList}
        onMouseDown={preventMouseDownDefault}
        onClick={toggleOrderedList}
      >
        <span className='rich-text-toolbar__glyph rich-text-toolbar__glyph--ordered-list'>
          1.≡
        </span>
      </button>

      <button
        type='button'
        className={classNames('rich-text-toolbar__button', {
          active: state.isLink,
        })}
        title={intl.formatMessage(messages.link)}
        aria-label={intl.formatMessage(messages.link)}
        aria-pressed={state.isLink}
        onMouseDown={preventMouseDownDefault}
        onClick={setLink}
      >
        <Icon id='link' icon={LinkIcon} />
      </button>

      <button
        type='button'
        className='rich-text-toolbar__button'
        title={intl.formatMessage(messages.outdent)}
        aria-label={intl.formatMessage(messages.outdent)}
        disabled={state.indentLevel <= 0}
        onMouseDown={preventMouseDownDefault}
        onClick={decreaseIndent}
      >
        <span className='rich-text-toolbar__glyph'>⇤</span>
      </button>

      <button
        type='button'
        className='rich-text-toolbar__button'
        title={intl.formatMessage(messages.indent)}
        aria-label={intl.formatMessage(messages.indent)}
        disabled={state.indentLevel >= MAX_INDENT_LEVEL}
        onMouseDown={preventMouseDownDefault}
        onClick={increaseIndent}
      >
        <span className='rich-text-toolbar__glyph'>⇥</span>
      </button>

      <button
        type='button'
        className='rich-text-toolbar__button'
        title={intl.formatMessage(messages.clearFormatting)}
        aria-label={intl.formatMessage(messages.clearFormatting)}
        onMouseDown={preventMouseDownDefault}
        onClick={clearFormatting}
      >
        <span className='rich-text-toolbar__glyph rich-text-toolbar__glyph--clear'>
          Tx
        </span>
      </button>
    </div>
  );
};
